// @flow
import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import { fetchIndexSeoStart, fetchIndexSeoSuccess, fetchIndexSeoFailed } from './actions';
import { FETCH_INDEX_SEO_REQUEST } from './constants';
import { LOCATION_CHANGE } from '../tracking/constants';
import clientConfig from '../../parameters/client/config';
import SearchApi from '../sdk/search/Api';
import KeywordsApi from '../sdk/keywords/Api';
import { dealTypeIdToString } from '../utils/dealType';
import ApiKeywordsError from '../sdk/keywords/ApiKeywordsError';
import ApiSearchError from '../sdk/search/ApiSearchError';
import { isServerSide } from '../utils/isomorphic';
import { handleSagaErrors } from '../Error/sagas';
import internalstatsOpenAlertApiTracking from '../tracking/events/internalstatsOpenAlert/apiTracking';
import internalstatsInternalClicksApiTracking from '../tracking/events/internalstatsInternalClicks/apiTracking';
import { hideGdprCookieBanner } from '../Gdpr/actions';
import { countryAndVerticalSelector } from '~/common/selectors/config';

const searchApi = new SearchApi(clientConfig.searchApi);
const keywordsApi = new KeywordsApi(clientConfig.keywordApi);

export function* getLinks(action: { data: Object, type: string }): Generator<*, *, *> {
  const { data } = action;
  try {
    yield put(fetchIndexSeoStart());
    // Complete the request data with values from config
    const countryAndVertical = yield select(countryAndVerticalSelector);
    const { countryCode, verticalName } = countryAndVertical;
    let response;
    let whereIn = [];
    let categoryId;
    let letter;
    const req = {
      country_code: countryCode,
      vertical_name: verticalName,
      browse_page: undefined,
      browse_category_id: undefined,
      browse_first_letter: undefined,
      dealType: undefined,
      homePageCategory: undefined,
      propertyType: undefined,
    };

    switch (verticalName) {
      case 'homes': {
        const { routeName, deal_type: dealType, property_type: propertyType } = data;
        const dealTypeString = dealTypeIdToString(dealType);
        const decodedPropertyType = propertyType ? decodeURI(propertyType) : undefined;

        req.dealType = dealType;
        req.homePageCategory =
          routeName === 'home' || !propertyType ? 'location' : 'location_with_type';
        req.propertyType =
          typeof decodedPropertyType === 'string'
            ? decodedPropertyType.replace(/-/g, ' ')
            : decodedPropertyType;

        response = yield call([searchApi, searchApi.homesSeoLinks], req);
        whereIn = decodedPropertyType ? [decodedPropertyType, dealTypeString] : [dealTypeString];
        break;
      }
      case 'cars': {
        response = yield call([searchApi, searchApi.carsSeoLinks], req);
        break;
      }
      case 'jobs': {
        response = yield call([searchApi, searchApi.jobsSeoLinks], req);
        break;
      }
      case 'products': {
        req.browse_page = data.page;
        req.browse_category_id = data.type;
        req.browse_first_letter = data.letter;

        response = yield call([keywordsApi, keywordsApi.browseProducts], req);
        categoryId = data.type;
        letter = data.letter;
        break;
      }
      default: {
        response = [];
        break;
      }
    }

    const noResultsExceptionMessage = 'No results for this query';
    if (response.length === 0 && isServerSide()) {
      if (verticalName === 'products') {
        throw new ApiKeywordsError(404, noResultsExceptionMessage, 404);
      }

      throw new ApiSearchError(404, noResultsExceptionMessage, 404);
    }

    yield put(
      fetchIndexSeoSuccess({
        response,
        whereIn,
        verticalName,
        categoryId,
        letter,
        numResults: response.length,
      })
    );
  } catch (error) {
    yield call(handleSagaErrors, error.code || error.status);
    yield put(fetchIndexSeoFailed(error));
  }
}

export function* sendTracking({ data }: { data: Object }): Generator<*, *, *> {
  const { query } = data;

  const internalstatsOpenAlertApiTrackingSender = yield select(internalstatsOpenAlertApiTracking);
  const internalstatsInternalClicksApiTrackingSender = yield select(
    internalstatsInternalClicksApiTracking
  );

  if (query.from === 'alert') {
    internalstatsOpenAlertApiTrackingSender.send();
  }
  if (typeof query.internalsection === 'string') {
    internalstatsInternalClicksApiTrackingSender.send();
  }
}

export function* hideGdprCookieBannerGenerator(): Generator<*, *, *> {
  yield put(hideGdprCookieBanner(true));
}

export default function* indexSagas(): Generator<*, *, *> {
  yield all([
    takeLatest(FETCH_INDEX_SEO_REQUEST, getLinks),
    takeLatest(LOCATION_CHANGE, sendTracking),
    takeLatest(LOCATION_CHANGE, hideGdprCookieBannerGenerator),
  ]);
}



// WEBPACK FOOTER //
// ./app/Index/sagas.js